import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {PageEvent} from '@angular/material/paginator';
import { NgxSpinnerService } from 'ngx-spinner';

import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { makeQueryBuilder, makeQueryRules } from '@ui/ui.module';

import { Term, Vocabulary } from '@atmi/core/models';
import { VocabularyService } from '@atmi/core/services/vocabulary.service';
import { VocabularyAdvancedManagerDialogComponent } from '@src/app/shared';

const PAGINATOR = { length: 10, pageIndex: 0, pageSize: 10 };

interface ActiveState {
  page: PageEvent;
  vocabulary?: Vocabulary;
  search?: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-vocabularies-manager',
  styleUrls: ['./vocabularies-manager.component.scss'],
  templateUrl: './vocabularies-manager.component.html',
})
export class VocabulariesManagerComponent implements OnDestroy {

  isOpening = false;

  private readonly activeState$ = new BehaviorSubject<ActiveState>({ page: PAGINATOR });
  private readonly refreshState$ = new BehaviorSubject(false);

  private readonly unsubscription$ = new Subject();

  readonly vocabularies$ = this.vocabularyApi.indexVocabulary();

  readonly activeTerms$ = combineLatest([
    this.activeState$.pipe(filter((state): state is ActiveState & { vocabulary: Vocabulary } => !!state.vocabulary)),
    this.refreshState$,
  ]).pipe(
    distinctUntilChanged(),
    switchMap(([state]) => {
      const rules = makeQueryRules(
        { valore: state.search, vocabolario: state.vocabulary.id },
        { valore: { operator: 'contains' }, vocabolario: { operator: 'equal', typeField: 'oid' } },
      );
      const query = makeQueryBuilder(rules);

      return state.search
        ? this.vocabularyApi.getTerms({ page: state.page.pageIndex + 1, limit: state.page.pageSize, filter: query })
        : this.vocabularyApi.getTermsFromVocabulary(state.vocabulary.id, { page: state.page.pageIndex + 1, limit: state.page.pageSize });
    }),
    debounceTime(1000),
    tap(() => {
      this.isOpening = false;
      this.spinnerService.hide('loadingTerms');
    }),
    takeUntil(this.unsubscription$),
  );

  readonly pageSizeOptions = [10, 25, 50];

  readonly advancedVocabularies = ['V_PRV'];

  get activeVocabulary() {
    return this.activeState$.value.vocabulary;
  }

  constructor(
    private readonly vocabularyApi: VocabularyService,
    private readonly spinnerService: NgxSpinnerService,
    private readonly dialog: MatDialog,
  ) {
  }

  ngOnDestroy() {
    this.unsubscription$.next(null);
    this.unsubscription$.complete();
  }

  isAdvancedVocabularyHandler(vocabulary: Vocabulary) {
    return this.advancedVocabularies.includes(vocabulary.nome);
  }

  openAdvancedVocabularyHandler(vocabulary: Vocabulary) {
    this.dialog.open(VocabularyAdvancedManagerDialogComponent, { data: { vocabulary }, width: '800px' });
  }

  changePageHandler(pageEvent: PageEvent) {
    this.setState({ page: pageEvent });
  }

  searchTermHandler(search: string) {
    this.isOpening = true;
    this.spinnerService.show('loadingTerms');

    this.setState({ search, page: PAGINATOR });
  }

  openSectionHandler(vocabulary: Vocabulary) {
    this.isOpening = true;
    this.spinnerService.show('loadingTerms');

    this.setState({ vocabulary, page: PAGINATOR, search: undefined });
  }

  addTermHandler(value: string, vocabulary: Vocabulary) {
    const newTerm: Term = {
      lingua: 'it_IT', // TODO: Remove embedded
      valore: value,
      vocabolario: vocabulary.id,
    };
    this.vocabularyApi.postTerm(newTerm).subscribe(() => {
      this.refreshState$.next(true);
    });
  }

  deleteTermHandler(_: Vocabulary, term: Term) {
    if (!term.id) {
      throw new Error('Cannot delete a term without a concrete value');
    }
    this.vocabularyApi.deleteTerm(term.id).subscribe(() => {
      this.refreshState$.next(true);
    });
  }

  trackByName(_: number, object: { id: string }) {
    return object.id;
  }

  private setState(partial: Partial<ActiveState>) {
    const old = this.activeState$.value;
    this.activeState$.next({ ...old, ...partial });
  }
}
